import React, { useEffect, useState } from "react";
import { api, PAYMENT_STATUSES, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function Payments() {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ quoted_price: 0, payment_paid: 0, payment_status: "Not Sent", payment_notes: "" });

  const load = async () => {
    const r = await api.get("/jobs");
    setJobs(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = jobs.filter((j) => !status || j.payment_status === status);

  const totalQuoted = filtered.reduce((s, j) => s + (j.quoted_price || 0), 0);
  const totalPaid = filtered.reduce((s, j) => s + (j.payment_paid || 0), 0);
  const overdue = filtered.filter((j) => j.payment_status === "Overdue").length;

  const open = (j) => {
    setEditing(j);
    setForm({
      quoted_price: j.quoted_price || 0,
      payment_paid: j.payment_paid || 0,
      payment_status: j.payment_status || "Not Sent",
      payment_notes: j.payment_notes || "",
    });
  };

  const save = async () => {
    try {
      await api.patch(`/jobs/${editing.id}`, {
        quoted_price: parseFloat(form.quoted_price) || 0,
        payment_paid: parseFloat(form.payment_paid) || 0,
        payment_status: form.payment_status,
        payment_notes: form.payment_notes,
      });
      toast.success("Payment updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Payments</h1>
        <p className="text-sm text-slate-500 mt-1">Track quotes, invoices, and payment status per job.</p>
      </div>

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <Stat label="Total quoted" value={`€${totalQuoted.toFixed(0)}`} />
        <Stat label="Total paid" value={`€${totalPaid.toFixed(0)}`} accent />
        <Stat label="Overdue jobs" value={overdue} warn />
      </div>

      <div className="mt-6 max-w-xs">
        <Select value={status} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger><SelectValue placeholder="All payment statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payment statuses</SelectItem>
            {PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job</TableHead><TableHead>Date</TableHead><TableHead>Quoted</TableHead>
              <TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((j) => (
              <TableRow key={j.id} className="cursor-pointer" onClick={() => open(j)} data-testid={`payment-row-${j.id}`}>
                <TableCell>
                  <div className="font-medium">{j.customer_name}</div>
                  <div className="text-xs text-slate-500">{j.service_type}</div>
                </TableCell>
                <TableCell>{j.date}</TableCell>
                <TableCell>€{j.quoted_price || 0}</TableCell>
                <TableCell>€{j.payment_paid || 0}</TableCell>
                <TableCell><StatusBadge status={j.payment_status} /></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost">Edit</Button></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-500">No jobs.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Payment — {editing?.customer_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Quoted (€)</Label><Input type="number" value={form.quoted_price} onChange={(e) => setForm({ ...form, quoted_price: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Paid (€)</Label><Input type="number" value={form.payment_paid} onChange={(e) => setForm({ ...form, payment_paid: e.target.value })} className="mt-1.5" data-testid="payment-paid-input" /></div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="payment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea value={form.payment_notes} onChange={(e) => setForm({ ...form, payment_notes: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="payment-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  const color = accent ? "text-emerald-700 bg-emerald-50 ring-emerald-200" : warn ? "text-rose-700 bg-rose-50 ring-rose-200" : "text-[#1B3A6B] bg-white ring-slate-200";
  return (
    <div className={`rounded-xl p-5 ring-1 ${color}`}>
      <div className="text-xs uppercase tracking-wide font-semibold opacity-80">{label}</div>
      <div className="mt-2 text-3xl font-heading font-bold">{value}</div>
    </div>
  );
}
